import React, { useMemo, useState, useEffect, useRef } from "react";
import { LogOut, Menu, ArrowRight, Lock, ChevronDown } from "lucide-react";
import ModalCambiarPassword from "../ModalCambiarPassword";
import { jwtDecode } from "jwt-decode";
import imagenLogo from "../../image/Logo-softnik-positivo-color.png";

const BADGE_THEMES = {
  teal:   { bg: "linear-gradient(135deg, #f0fdfa, #ccfbf1)", border: "#99f6e4", color: "#0f766e" },
  indigo: { bg: "linear-gradient(135deg, #eef2ff, #e0e7ff)", border: "#c7d2fe", color: "#4338ca" },
  green:  { bg: "linear-gradient(135deg, #ecfdf5, #d1fae5)", border: "#a7f3d0", color: "#047857" },
  purple: { bg: "linear-gradient(135deg, #faf5ff, #f3e8ff)", border: "#e9d5ff", color: "#10233B" },
};

export default function PortalHeader({
  userData,
  companyName,
  portalTitle = "Portal",
  badgeColor = "teal",
  BadgeIcon,
  onLogout,
  onToggleSidebar,
  showSwitchToApp = false,
  tokenKey,
}) {
  const theme = BADGE_THEMES[badgeColor] || BADGE_THEMES.teal;
  const [showDropdown, setShowDropdown] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const dropdownRef = useRef(null);

  const isAdmin = useMemo(() => {
    if (!showSwitchToApp || !tokenKey) return false;
    try {
      const token = sessionStorage.getItem(tokenKey);
      if (!token) return false;
      const payload = jwtDecode(token);
      const roles = payload.roles?.map((r) => r.authority) || [];
      return roles.includes("ROLE_ADMIN");
    } catch {
      return false;
    }
  }, [showSwitchToApp, tokenKey]);

  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  const handleSwitchToApp = () => {
    if (!tokenKey) return;
    const token = sessionStorage.getItem(tokenKey);
    if (!token) return;
    try {
      const payload = jwtDecode(token);
      sessionStorage.setItem("access_token", token);
      sessionStorage.setItem("appUserRoles", "ROLE_ADMIN");
      sessionStorage.setItem("id", String(payload.id || ""));
      sessionStorage.setItem("email", payload.sub || "");
      sessionStorage.setItem("company", payload.company || "");
      sessionStorage.setItem("username", payload.username || "");
      sessionStorage.setItem("department", payload.department || "");
      sessionStorage.setItem("area", payload.area || "");
      window.location.href = "/home";
    } catch (e) {
      console.error("Error switching to main app:", e);
    }
  };

  const userName = userData?.nombre || userData?.name || "";
  const initials = userName
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");

  return (
    <header className="portal-header">
      <div className="portal-header-left">
        {onToggleSidebar && (
          <button className="portal-header-menu-toggle" onClick={onToggleSidebar} type="button">
            <Menu size={20} />
          </button>
        )}
        <img src={imagenLogo} alt="Softnik" className="portal-header-logo" />
        {companyName && (
          <div
            className="portal-header-badge portal-header-badge-desktop"
            style={{ background: theme.bg, borderColor: theme.border, color: theme.color }}
          >
            {BadgeIcon && <BadgeIcon size={16} />}
            {companyName}
          </div>
        )}
      </div>
      <div className="portal-header-right">
        {isAdmin && (
          <button
            className="portal-header-btn-switch"
            onClick={handleSwitchToApp}
            type="button"
            style={{ background: theme.bg, borderColor: theme.border, color: theme.color }}
          >
            <ArrowRight size={16} />
            <span>Ir a la app</span>
          </button>
        )}
        <div className="portal-user-dropdown-wrapper" ref={dropdownRef}>
          <button
            className="portal-user-toggle"
            onClick={() => setShowDropdown((prev) => !prev)}
            type="button"
          >
            <div className="portal-user-avatar">{initials || "U"}</div>
            <div className="portal-user-toggle-info">
              <span className="portal-user-toggle-name">{userName}</span>
              <span className="portal-user-toggle-role">{portalTitle}</span>
            </div>
            <ChevronDown size={14} className={`portal-user-chevron ${showDropdown ? "open" : ""}`} />
          </button>

          {showDropdown && (
            <div className="portal-user-dropdown">
              <div className="portal-user-dropdown-header">
                <div className="portal-user-dropdown-avatar">{initials || "U"}</div>
                <div className="portal-user-dropdown-info">
                  <span className="portal-user-dropdown-name">{userName}</span>
                  <span className="portal-user-dropdown-email">{portalTitle}</span>
                </div>
              </div>
              <div className="portal-user-dropdown-divider" />
              <button
                className="portal-user-dropdown-item"
                onClick={() => { setShowDropdown(false); setShowChangePassword(true); }}
              >
                <Lock size={15} />
                <span>Cambiar contraseña</span>
              </button>
              <div className="portal-user-dropdown-divider" />
              <button
                className="portal-user-dropdown-item portal-user-dropdown-logout"
                onClick={() => { setShowDropdown(false); onLogout?.(); }}
              >
                <LogOut size={15} />
                <span>Cerrar sesión</span>
              </button>
            </div>
          )}
        </div>
      </div>
      {companyName && (
        <div
          className="portal-header-badge portal-header-badge-mobile"
          style={{ background: theme.bg, borderColor: theme.border, color: theme.color }}
        >
          {BadgeIcon && <BadgeIcon size={14} />}
          {companyName}
        </div>
      )}
      <ModalCambiarPassword show={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </header>
  );
}
